import React from 'react';

/**
 * Button - Unified button component with variants
 *
 * Variants:
 * - primary: Blue action buttons (Calculate Fit, Save Build)
 * - secondary: Subtle zinc buttons (Sign In, Refresh)
 * - ghost: Minimal buttons for toggles with active state
 * - success: Green buttons (shopping links, positive actions)
 * - danger: Red buttons (Delete, Clear)
 *
 * Sizes: sm, md, lg
 */

const variantStyles = {
  primary: 'bg-primary hover:bg-primary-hover text-white shadow-lg shadow-primary/20',
  secondary: 'bg-subtle hover:bg-highlight text-zinc-300 hover:text-white',
  ghost: 'bg-transparent hover:bg-subtle text-zinc-400 hover:text-white',
  success: 'bg-success hover:bg-success-hover text-white shadow-lg shadow-success/20',
  danger: 'bg-error hover:bg-error-hover text-white',
  warning: 'bg-warning hover:bg-warning-hover text-white',
};

const activeVariantStyles = {
  primary: 'bg-primary text-white',
  secondary: 'bg-highlight text-white',
  ghost: 'bg-subtle text-white',
  success: 'bg-success text-white',
  danger: 'bg-error text-white',
  warning: 'bg-warning text-white',
};

const sizeStyles = {
  sm: 'px-2 py-1 text-xs rounded',
  md: 'px-3 py-2 text-sm rounded-lg',
  lg: 'px-4 py-3 text-base rounded-lg font-bold',
  xl: 'px-6 py-4 text-base rounded-lg font-bold w-full',
};

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  active = false,
  disabled = false,
  fullWidth = false,
  className = '',
  icon: Icon,
  iconRight = false,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center gap-2 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const variantStyle = active ? activeVariantStyles[variant] : variantStyles[variant];
  const sizeStyle = sizeStyles[size];
  const widthStyle = fullWidth ? 'w-full' : '';

  return (
    <button
      className={`${baseStyles} ${variantStyle} ${sizeStyle} ${widthStyle} ${className}`}
      disabled={disabled}
      {...props}
    >
      {Icon && !iconRight && <Icon size={size === 'sm' ? 12 : size === 'lg' || size === 'xl' ? 18 : 14} />}
      {children}
      {Icon && iconRight && <Icon size={size === 'sm' ? 12 : size === 'lg' || size === 'xl' ? 18 : 14} />}
    </button>
  );
};

/**
 * ButtonGroup - Container for toggle buttons
 */
export const ButtonGroup = ({ children, className = "" }) => (
  <div className={`flex bg-card-solid p-1 rounded-lg border border-subtle ${className}`}>
    {children}
  </div>
);

/**
 * ToggleButton - Button within a ButtonGroup for segmented controls
 * Used for: LH/RH, Wood/Iron fit type, Manual/CSV/OCR mode
 */
export const ToggleButton = ({
  children,
  active = false,
  activeColor = 'bg-primary',
  className = '',
  ...props
}) => (
  <button
    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active ? `${activeColor} text-white` : 'text-zinc-400 hover:text-zinc-200'
    } ${className}`}
    {...props}
  >
    {children}
  </button>
);

/**
 * IconButton - Round button with just an icon
 */
export const IconButton = ({
  icon: Icon,
  size = 'md',
  variant = 'ghost',
  className = '',
  title = '',
  ...props
}) => {
  const iconSizes = { sm: 12, md: 16, lg: 20 };
  const paddings = { sm: 'p-1.5', md: 'p-2.5', lg: 'p-3' };

  return (
    <button
      className={`${variantStyles[variant]} ${paddings[size]} rounded-full transition-colors ${className}`}
      title={title}
      {...props}
    >
      <Icon size={iconSizes[size]} />
    </button>
  );
};
